import * as chemistryApi from '@/api/chemistry';
import FileTreeTitleRenderer from '@/components/fileTreeTitleRenderer';
import { fileService } from '@/utils/filesService';
import useFileStore from '@/utils/fileStore';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { App, Button, Input, Modal, Select, Space, Tree, TreeDataNode } from 'antd';
import pathbe from 'path-browserify-esm';
import React, { useEffect, useState } from 'react';

const OPENBABEL_OUT_FORMATS = [
    { value: 'xyz', label: 'XYZ (.xyz)' },
    { value: 'pdb', label: 'PDB (.pdb)' },
    { value: 'sdf', label: 'SDF (.sdf)' },
    { value: 'mol2', label: 'Mol2 (.mol2)' },
    { value: 'mol', label: 'MDL MOL (.mol)' },
    { value: 'pqr', label: 'PQR (.pqr)' },
    { value: 'cube', label: 'Gaussian Cube (.cube)' },
    { value: 'cif', label: 'CIF (.cif)' },
    { value: 'can', label: 'Canonical SMILES (.can)' },
    { value: 'smi', label: 'SMILES (.smi)' },
    { value: 'gro', label: 'GRO (.gro)' },
    { value: 'inchi', label: 'InChI (.inchi)' },
    { value: 'json', label: 'ChemDoodle JSON (.json)' },
    { value: 'svg', label: 'SVG Image (.svg)' },
    { value: 'png', label: 'PNG Image (.png)' },
];

interface ConvertDialogProps {
    open: boolean;
    sourcePath: string;
    onCancel: () => void;
    onSuccess: (outputPath: string) => void;
}

const ConvertDialog: React.FC<ConvertDialogProps> = ({ open, sourcePath, onCancel, onSuccess }) => {
    const { message } = App.useApp();
    const fileTree = useFileStore(state => state.fileTree);
    const expandedKeys = useFileStore(state => state.expandedKeys);
    const openFolder = useFileStore(state => state.openFolder);
    const isFileNew = useFileStore(state => state.isFileNew);
    const doesFolderContainNewFiles = useFileStore(state => state.doesFolderContainNewFiles);
    const updateFolder = useFileStore(state => state.updateFolder);

    const sourceExt = pathbe.extname(sourcePath).toLowerCase();
    const sourceBasename = pathbe.basename(sourcePath, sourceExt);
    const sourceDir = pathbe.dirname(sourcePath);

    const [targetFormat, setTargetFormat] = useState<string | undefined>(undefined);
    const [outputFileName, setOutputFileName] = useState<string>('');
    const [selectedDir, setSelectedDir] = useState<string>('');
    const [converting, setConverting] = useState(false);

    // Reset state when dialog opens with a new source file
    useEffect(() => {
        if (open) {
            setTargetFormat(undefined);
            setOutputFileName('');
            setSelectedDir(sourceDir);
        }
    }, [open, sourcePath, sourceDir]);

    // Update filename when format changes
    useEffect(() => {
        if (targetFormat) {
            setOutputFileName(`${sourceBasename}.${targetFormat}`);
        }
    }, [targetFormat, sourceBasename]);

    const handleDirSelect = (keys: React.Key[]) => {
        if (keys.length > 0) {
            setSelectedDir(keys[0] as string);
        }
    };

    const handleConfirm = async () => {
        if (!targetFormat) {
            message.warning('Please select a target format');
            return;
        }
        const dir = selectedDir || sourceDir;
        const filename = outputFileName.trim() || `${sourceBasename}.${targetFormat}`;
        const outputPath = `${dir}/${filename}`;

        setConverting(true);
        try {
            const res = await chemistryApi.convertMolecule({
                path: sourcePath,
                to_format: targetFormat,
                output_path: outputPath,
            });
            if (res?.ok) {
                const savedPath = res.data?.output_path ?? outputPath;
                message.success(`Converted and saved as ${pathbe.basename(savedPath)}`);
                updateFolder(pathbe.dirname(savedPath));
                onSuccess(savedPath);
            } else {
                message.error(res?.message ?? 'Conversion failed');
            }
        } catch (err) {
            message.error(`Conversion failed: ${String(err)}`);
        } finally {
            setConverting(false);
        }
    };

    // dnd-kit sensor — used only to satisfy DndContext requirement for FileTreeTitleRenderer
    const sensor = useSensor(PointerSensor, { activationConstraint: { distance: 9999 } });

    const noop = () => { };
    const noopAsync = async () => { };

    const outputPreview = (() => {
        const dir = selectedDir || sourceDir;
        const filename = outputFileName.trim() || (targetFormat ? `${sourceBasename}.${targetFormat}` : '');
        return filename ? `${dir}/${filename}` : '';
    })();

    return (
        <Modal
            title="Convert File Format"
            open={open}
            onCancel={onCancel}
            destroyOnClose
            footer={
                <div className="flex flex-row-reverse">
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={converting}
                            disabled={!targetFormat}
                            onClick={handleConfirm}
                        >
                            Convert
                        </Button>
                    </Space>
                </div>
            }
            width={520}
        >
            <div className="space-y-4 py-2">
                {/* Source file */}
                <div>
                    <p className="text-xs text-gray-500 dark:text-[#999999] mb-1">Source file</p>
                    <p className="text-sm font-mono truncate" title={sourcePath}>{sourcePath}</p>
                </div>

                {/* Target format */}
                <div>
                    <p className="text-xs text-gray-500 dark:text-[#999999] mb-1">Target format</p>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select target format"
                        value={targetFormat}
                        onChange={(v) => setTargetFormat(v)}
                        options={OPENBABEL_OUT_FORMATS}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </div>

                {/* Output filename */}
                <div>
                    <p className="text-xs text-gray-500 dark:text-[#999999] mb-1">Output filename</p>
                    <Input
                        value={outputFileName}
                        onChange={(e) => setOutputFileName(e.target.value)}
                        placeholder={targetFormat ? `${sourceBasename}.${targetFormat}` : 'Select a format first'}
                        disabled={!targetFormat}
                    />
                </div>

                {/* Output directory selector */}
                <div>
                    <p className="text-xs text-gray-500 dark:text-[#999999] mb-1">Output directory</p>
                    <div className="border border-gray-200 dark:border-[#404040] rounded h-48 overflow-auto bg-white dark:bg-[#252526]">
                        <DndContext sensors={[sensor]}>
                            <Tree
                                treeData={fileTree?.children as TreeDataNode[] | undefined}
                                expandedKeys={expandedKeys}
                                selectedKeys={selectedDir ? [selectedDir] : []}
                                onSelect={handleDirSelect}
                                onExpand={(keys) => {
                                    // expand/collapse handled by openFolder via FileTreeTitleRenderer click
                                    const newKey = keys.find(k => !expandedKeys.includes(k as string));
                                    if (newKey) openFolder(newKey as string);
                                }}
                                showIcon={false}
                                blockNode
                                titleRender={(node: any) => (
                                    <FileTreeTitleRenderer
                                        node={node}
                                        selectedKeys={selectedDir ? [selectedDir] : []}
                                        expandedKeys={expandedKeys}
                                        openFolder={openFolder}
                                        isFileNew={isFileNew}
                                        doesFolderContainNewFiles={doesFolderContainNewFiles}
                                        setFileMenuVisibleKey={noop}
                                        handleCreateDialogOpen={noop as any}
                                        setUploadDialogVisible={noop}
                                        handleDrop={noopAsync as any}
                                        handleDragOverInTree={noop as any}
                                        onMouseUp={(key) => {
                                            if (node.isDir) {
                                                setSelectedDir(key);
                                            }
                                        }}
                                    />
                                )}
                            />
                        </DndContext>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-[#858585] mt-1">
                        Selected: <span className="font-mono">{selectedDir || sourceDir || fileService.baseHome}</span>
                    </p>
                </div>

                {/* Output path preview */}
                {outputPreview && (
                    <div>
                        <p className="text-xs text-gray-500 dark:text-[#999999] mb-1">Output path preview</p>
                        <p className="text-xs font-mono text-blue-600 dark:text-[#3794DE] break-all">{outputPreview}</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ConvertDialog;
